import { delay } from '@/core';

export default {
	data() {
		let columnDefinitions = this._transformDefinitions(this.columnDefinitions);
		return {
			localColumnDefinitions: columnDefinitions,
			localColumns: (this.columns) ? this.columns : this._buildLocalColumns(columnDefinitions, this.localStorageId),
		};
	},
	props: {
		/**
		 * Array containing default column definitions.
		 * @see See [Column Definition](#Grid-ColumnDefinition)
		 */
		defaultColumnDefinition: [Object],

		/**
		 * Array containing column definitions.
		 * @see See [Column Definition](#Grid-ColumnDefinition)
		 */
		columnDefinitions: {
			type: Array,
			default: () => []
		},

		/**
		 * Array containing which order to display columns in, user width of each column, and sort order / sort direction of the column
		 */
		columns: Array,
		/**
		 * String id used to save column defintions to local storage
		 */
		localStorageId: { type: String }
	},
	computed: {
		columnStyles() {
			let result = {};
			for (let column of this.localColumns) {
				result[column.field] = {};
				if (column.center)
					result[column.field].justifyContent = 'center';
				if (column.width)
					result[column.field].width = `${column.width}px`;
				if (column.minWidth)
					result[column.field].minWidth = `${column.minWidth}px`;
			}
			return result;
		},

		columnDisplayNames() {
			let result = {};
			for (let columnName in this.localColumnDefinitions) {
				let column = this.localColumnDefinitions[columnName];
				if ('displayName' in column) {
					result[column.field] = column.displayName;
				} else {
					let name = column.field;
					name = name.replace(/([A-Z])/g, ' $1');
					name = name.charAt(0).toUpperCase() + name.substring(1);
					result[column.field] = name;
				}
			}
			return result;
		},
	},
	methods: {
		_getColumnClass(column) {
			let columnDef = this.localColumnDefinitions[column.field] || {};
			let c = ['grid-header-cell'];
			if (columnDef.type)
				c.push(`grid-header-cell-type-${columnDef.type}`);
			return c.join(' ');
		},

		_getColumnHeaderAlignStyle(column) {
			let result = {};
			let columnDef = this.localColumnDefinitions[column.field] || {};
			if (columnDef.align)
				result.textAlign = columnDef.align;
			return result;
		},

		_calculateAllWidths() {
			this.showHeaders = false;
			this.showRows = false;
			this.$nextTick(async() => {
				let parentWidth = this.$parent.$el.offsetWidth;
				for (let i = 0; i < 6; i++) {
					if (parentWidth !== 0) break;
					await delay(10);
					parentWidth = this.$parent.$el.offsetWidth;
				}
				let usableWidth = parentWidth;
				if (this.$refs.gridContainer) {
					usableWidth = Math.min(this.$refs.gridContainer.offsetWidth, parentWidth);
				}
				let totalWidth = usableWidth - 6;
				if (this.checkboxVisible)
					totalWidth -= 45;
				let takenWidth = this.localColumns.reduce((p, c) => p + (c.width || 0), 0);
				let numFlexColumns = this.localColumns.reduce((p,c) => (c.width) ? p : p+1, 0);
				let flexWidth = Math.max(100, Math.floor((totalWidth - takenWidth) / numFlexColumns));
				
				for (let column of this.localColumns) {
					if (column.width) continue;
					column.width = flexWidth;
				}

				this.showHeaders = true;
				this.showRows = true;
				this.gridWidth = totalWidth;
			});
		},

		_storeLocalColumns() {
			if (!this.localStorageId) return;
			let columnsToStore = this.localColumns.map(x => ({ field: x.field, width: x.width }));
			localStorage.setItem(`${this.localStorageId}/localColumns`, JSON.stringify(columnsToStore));
		},
		_buildLocalColumns(columnDefinitions,localStorageId) {
			let localColumns = undefined;
			if (localStorageId) {
				let localColumnsJSON = localStorage.getItem(`${localStorageId}/localColumns`);
				if (localColumnsJSON)
					localColumns = JSON.parse(localColumnsJSON);
			}

			if (localColumns)
				return this._mergeExtraColumnsFromDefinitions(localColumns, columnDefinitions);
			else
				return this._buildLocalColumnsFromDefinitions(columnDefinitions);
		},
		_mergeExtraColumnsFromDefinitions(columns, columnDefinitions) {
			let missingColumnFields = Object.keys(columnDefinitions).filter(x => columns.every(y => y.field !== x));
			let missingColumnDefs = {};
			for (let missingColumnField of missingColumnFields) {
				missingColumnDefs[missingColumnField] = columnDefinitions[missingColumnField];
			}
			let missingColumns = this._buildLocalColumnsFromDefinitions(missingColumnDefs);
			let firstNonLockedIndex = columns.length;
			for (let i = columns.length - 1; i >= 0; i--) {
				if (columns[i].locked !== 'right') continue;

				firstNonLockedIndex = i;
				break;
			}

			return columns.slice(0, firstNonLockedIndex+1).concat(missingColumns, columns.slice(firstNonLockedIndex+1));
		},

		_buildLocalColumnsFromDefinitions(columnDefinitions) {
			let result = [];
			let rightLocked = [];
			let lastLockedLeft = 0;
			for (let field in columnDefinitions) {
				let column = { field: field, width: columnDefinitions[field].width, minWidth: columnDefinitions[field].minWidth, locked: columnDefinitions[field].locked };
				if (columnDefinitions[field].locked === 'left')
					result.splice(lastLockedLeft++, 0, column);
				else if (columnDefinitions[field].locked === 'right')
					rightLocked.push(column);
				else
					result.push(column);
			}
			for (let column of rightLocked) {
				result.push(column);
			}

			return result;
		},
		_handleNewColumns(columns) {
			if (columns === undefined || columns === null)
				columns = this._buildLocalColumns(this.localColumnDefinitions,this.localStorageId);
			let invalidColumns = [];
			let validColumns = [];
			for (let column of columns) {
				if (!this.localColumnDefinitions[column.field]) {
					console.warn(`Column ${column.field} is not defined in columnDefinitions.`);
					invalidColumns.push(column.field);
					continue;
				}
				if (!column.width)
					column.width = this.localColumnDefinitions[column.field].width;
				validColumns.push(column);
			}
			if (invalidColumns.length > 0) {
				/**
				 * When columns-prop contains invalid columns. Payload is array of missing columns.
				 * @event columns-invalid
				 * @type {array}
				 */
				this.$emit('columns-invalid', invalidColumns);
			}
			validColumns = this._mergeExtraColumnsFromDefinitions(validColumns, this.localColumnDefinitions);
			this.$set(this, 'localColumns', validColumns);
			this._calculateAllWidths();
			this._storeLocalColumns();
			return invalidColumns.length === 0;
		},

	},
	watch: {
		defaultColumnDefinition(newValue, oldValue) {
			if (newValue === oldValue) return;
			for (let columnName in this.localColumnDefinitions) {
				this.localColumnDefinitions[columnName] = { ...newValue, ...this.localColumnDefinitions[columnName] };
			}
		},
		columnDefinitions: {
			immediate: true,
			handler(newValue, oldValue) {
				if (newValue === oldValue) return;
				this.$set(this, 'localColumnDefinitions', this._transformDefinitions(newValue));
				this._handleNewColumns(this.columns);
			}
		},
		columns: {
			immediate: true,
			handler(newValue, oldValue) {
				if (newValue === oldValue) return;
				this._handleNewColumns(newValue);
			}
		},
		localStorageId(newValue, oldValue) {
			if (newValue === oldValue) return;
			//this.localColumns = this._buildLocalColumns(this.localColumnDefinitions, newValue);
		}
	}
};